import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ActivationService,
    AuthenticationService,
    EspaceClientService,
    User,
} from '@kv-common-components/kx-services';
import { Caisse } from '@modules/connexion/caisse-auto-complete/caisse';
import { PersonneHelperService } from '@shared/services/personne-helper.service';
import { NGXLogger } from 'ngx-logger';
import { crcaList } from '../connexion/caisse-auto-complete/crca-list';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-activation',
    templateUrl: 'activation.component.html',
})
export class ActivationComponent implements OnInit, OnDestroy {
    accountActivationForm: FormGroup;
    isLoading = false;
    apporteurPrincipal: string;
    idPersonne: string;
    apporteurSecondaire: string;
    token: string;
    selectedCaisse: Caisse;
    submitLoading = false;
    sendSMSSucces = false;
    espaceClientSubscription: Subscription;
    queryParamsSubscription: Subscription;

    constructor(
        private logger: NGXLogger,
        private authenticationService: AuthenticationService,
        private activationService: ActivationService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private espaceClientService: EspaceClientService,
        private personneHelper: PersonneHelperService
    ) {}

    ngOnInit(): void {
        const otpControl = new FormControl();
        otpControl.setValidators([
            Validators.maxLength(20),
            Validators.required,
        ]);
        this.accountActivationForm = new FormGroup({});
        this.accountActivationForm.addControl('otp', otpControl);
        this.queryParamsSubscription =
            this.activatedRoute.queryParams.subscribe((params) => {
                this.logger.debug('Activation infos received from url : ', {
                    params,
                });
                this.apporteurPrincipal = params.apporteurPrincipal;
                this.idPersonne = params.idPersonne;
                this.apporteurSecondaire = params.apporteurSecondaire;
                this.token = params.token;
                this.selectedCaisse = crcaList.find(
                    (x) => x.id === this.apporteurPrincipal
                );
                this.demandeActivation(false);
            });
    }

    ngOnDestroy() {
        this.apporteurPrincipal = null;
        this.apporteurSecondaire = null;
        this.idPersonne = null;
        this.token = null;
        this.selectedCaisse = null;

        if (this.espaceClientSubscription) {
            this.espaceClientSubscription.unsubscribe();
        }
        if (this.queryParamsSubscription) {
            this.queryParamsSubscription.unsubscribe();
        }
    }

    demandeActivation(demandeManuel: boolean) {
        if (!this.submitLoading) {
            this.submitLoading = true;
            this.activationService
                .demandeActivation(
                    this.idPersonne,
                    this.apporteurPrincipal,
                    this.apporteurSecondaire,
                    this.token
                )
                .subscribe(
                    (response: any) => {
                        this.logger.debug(
                            'Response from demandeActivation : ',
                            {
                                response,
                            }
                        );
                        this.submitLoading = false;
                        if (response !== 'ACCEPTED') {
                            throw response;
                        }
                        if (demandeManuel) {
                            this.sendSMSSucces = true;
                        }
                    },
                    (error) => this.handleError(error)
                );
        }
    }

    submitActivation() {
        if (!this.submitLoading) {
            this.submitLoading = true;
            this.logger.info(
                'Activation submited for person: ',
                this.idPersonne,
                ' token : ',
                this.token
            );
            this.activationService
                .activation(
                    this.idPersonne,
                    this.apporteurPrincipal,
                    this.apporteurSecondaire,
                    this.token,
                    this.otp.value.trim(),
                    this.newPassword.value
                )
                .subscribe(
                    (personne: any) => {
                        this.logger.info('Approved activation for : ', {
                            personne,
                        });
                        this.login(
                            personne.telephone,
                            this.newPassword.value,
                            this.apporteurPrincipal
                        );
                    },
                    (error) => this.handleError(error)
                );
        }
    }
    private login(phone, password, caisseId) {
        this.authenticationService.login(phone, password, caisseId).subscribe(
            (user: User) => {
                this.logger.info('User: ', { user }, ' succesfully logged');
                this.espaceClientSubscription = this.espaceClientService
                    .refreshEspaceClient(user.idPersonne)
                    .subscribe((data) => {
                        this.personneHelper.initCurrentUser(data, user);
                        this.submitLoading = false;
                        this.router.navigateByUrl(
                            'espace-client/tableau-de-bord'
                        );
                    });
            },
            (error) => this.handleError(error)
        );
    }

    get otp() {
        return this.accountActivationForm.get('otp');
    }

    get newPassword() {
        return this.accountActivationForm.get('newPassword');
    }

    get repeatPassword() {
        return this.accountActivationForm.get('repeatPassword');
    }

    private handleError(error) {
        this.submitLoading = false;

        // Utilise optional chaining pour éviter les erreurs d'accès
        if (error?.error?.erreursFonctionnelle?.[0]?.code === 'KN_PEO_017') {
            // Account was already activated
            this.router.navigateByUrl('/connexion');
            return;
        }
        throw error;
    }
}
